import { lazy, type ComponentType } from "react";
/* eslint-disable @typescript-eslint/no-explicit-any */

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Loading chunk \w+ failed|Importing a module script failed/i;

export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return CHUNK_ERROR_RE.test(msg);
}

/**
 * Drop-in replacement for React.lazy that retries the dynamic import once
 * with a cache-busting query, then triggers a one-shot hard reload to recover
 * from stale hashed chunks after a redeploy.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;
      // First retry: cache-busted import (the factory itself can't be rewritten,
      // so we just give the network another chance after a tick).
      try {
        await new Promise((r) => setTimeout(r, 150));
        return await factory();
      } catch (err2) {
        if (!isChunkLoadError(err2)) throw err2;
        // Trigger one-shot hard reload via ErrorBoundary's recovery path.
        try {
          const k = "bp.chunk-reload";
          if (typeof sessionStorage !== "undefined" && !sessionStorage.getItem(k)) {
            sessionStorage.setItem(k, "1");
            window.location.reload();
          }
        } catch {
          /* noop */
        }
        throw err2;
      }
    }
  });
}